import { useCallback, useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import styles from "./styles.module.scss";
import Table, { TableColumn } from "../../contract/list_v2/Table";
import Pagination from "../../../components/pagination";
import Spinner from "../../../components/common/Spinner";
import AutoConsentDecisionModal, {
  AutoConsentDecision,
} from "../../../components/organisations/AutoConsentDecisionModal";
import ClientIdLabel from "../settings/components/ClientsInformation/ClientIdLabel";
import { ROUTES } from "../../../setup/constants";
import { canAct } from "../../../setup/auth/oidc";
import {
  approveOrganizationRequest,
  getOrganizationRequests,
  rejectOrganizationRequest,
  getRequestErrorMessage,
} from "../../../setup/api/services/organization";
import {
  IOrganizationRequestAdmin,
  OrganizationRequestStatus,
} from "../../../setup/api/services/organization/models";

const PAGE_SIZE = 10;

interface ISettingRequestsTabProps {
  /** Called after a decision so the tab badge can refresh its count. */
  onChanged: () => void;
}

interface IPendingDecision {
  request: IOrganizationRequestAdmin;
  decision: AutoConsentDecision;
}

const formatDate = (value?: string | null) =>
  value ? new Date(value).toLocaleDateString("en-GB") : "-";

/**
 * The "setting requests" tab of the Organizations page: the orgs' requests for a change, newest
 * first. Today the only type is automatic consent on one API client. Staff approve (which switches
 * the client's automatic consent on and approves its waiting customers), reject with a reason, or
 * open the org's API clients tab to look at the client first. What a row shows for a type comes
 * from the request's `details`, which the backend's handler for that type fills in.
 */
const SettingRequestsTab = ({ onChanged }: ISettingRequestsTabProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [status, setStatus] = useState<OrganizationRequestStatus | undefined>(
    "PENDING"
  );
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [rows, setRows] = useState<IOrganizationRequestAdmin[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pending, setPending] = useState<IPendingDecision | null>(null);
  const [saving, setSaving] = useState(false);
  const mayDecide = canAct();

  const fetchRequests = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getOrganizationRequests(
        status,
        page - 1,
        PAGE_SIZE
      );
      setRows(response?.data?.content ?? []);
      setPageCount(response?.data?.totalPages || 1);
    } catch (error) {
      console.error(error);
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  }, [status, page]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleConfirm = async (reason: string) => {
    if (!pending) return;
    const { request, decision } = pending;
    setPending(null);
    setSaving(true);
    try {
      if (decision === "approve") {
        const response = await approveOrganizationRequest(request.id, reason);
        toast.success(
          t("lbl_auto_consent_approved_toast", {
            count: response?.data?.approvedLinks ?? 0,
          })
        );
      } else {
        await rejectOrganizationRequest(request.id, reason);
        toast.success(t("lbl_auto_consent_rejected_toast"));
      }
      onChanged();
      fetchRequests();
    } catch (error) {
      console.error(error);
      // The request may already be decided or cancelled elsewhere; say why and show its real state
      toast.error(
        getRequestErrorMessage(error) ?? t("lbl_auto_consent_decision_failed")
      );
      onChanged();
      fetchRequests();
    } finally {
      setSaving(false);
    }
  };

  const openSettings = (row: IOrganizationRequestAdmin) =>
    navigate(
      `${ROUTES.ORGANIZATIONS_DETAILS.replace(
        ":code",
        row.organizationCode
      )}?tab=api-clients&client=${encodeURIComponent(row.subject ?? "")}`
    );

  const columns: TableColumn<IOrganizationRequestAdmin>[] = useMemo(
    () => [
      {
        key: "organization",
        label: t("lbl_auto_consent_col_org"),
        render: (_value, row) => (
          <div className={styles.orgCell}>
            <span className={styles.orgName}>
              {row.organizationName || row.organizationCode}
            </span>
            <span
              className={styles.orgCode}
              dir="ltr">{`#${row.organizationCode}`}</span>
          </div>
        ),
      },
      {
        key: "type",
        label: t("lbl_request_col_type"),
        render: (value) => <span>{t(`lbl_request_type_${value}`)}</span>,
      },
      {
        key: "subject",
        label: t("lbl_auto_consent_col_client"),
        render: (_value, row) => (
          <div className={styles.clientCell}>
            {row.subject && <ClientIdLabel clientId={row.subject} />}
            {row.details?.clientDescription && (
              <span className={styles.clientDescription}>
                {row.details.clientDescription}
              </span>
            )}
          </div>
        ),
      },
      {
        key: "reason",
        label: t("lbl_auto_consent_col_use_case"),
        render: (value) => (
          <span className={styles.useCase} title={value}>
            {value}
          </span>
        ),
      },
      {
        key: "requestedAt",
        label: t("lbl_auto_consent_col_date"),
        render: (value) => <span>{formatDate(value)}</span>,
      },
      {
        key: "waitingLinks",
        label: t("lbl_auto_consent_col_waiting"),
        render: (_value, row) => (
          <span>{row.details?.waitingLinks ?? "-"}</span>
        ),
      },
      {
        key: "status",
        label: t("lbl_auto_consent_col_status"),
        render: (value, row) => (
          <div className={styles.statusCell}>
            <span
              className={classNames(styles.statusPill, {
                [styles.pillPending]: value === "PENDING",
                [styles.pillApproved]: value === "APPROVED",
                [styles.pillRejected]: value === "REJECTED",
                [styles.pillCancelled]:
                  value === "CANCELLED" || value === "EXPIRED",
              })}>
              {t(`lbl_auto_consent_status_${value}`)}
            </span>
            {row.decisionReason && (
              <span
                className={styles.decisionReason}
                title={row.decisionReason}>
                {row.decisionReason}
              </span>
            )}
          </div>
        ),
      },
      {
        key: "actions",
        label: t("procedures"),
        render: (_value, row) => (
          <div className={styles.actionCell}>
            {row.status === "PENDING" && mayDecide && (
              <>
                <span
                  className={styles.actionText}
                  onClick={(e) => {
                    e.stopPropagation();
                    setPending({ request: row, decision: "approve" });
                  }}>
                  {t("lbl_auto_consent_approve")}
                </span>
                <span
                  className={classNames(styles.actionText, styles.danger)}
                  onClick={(e) => {
                    e.stopPropagation();
                    setPending({ request: row, decision: "reject" });
                  }}>
                  {t("lbl_auto_consent_reject")}
                </span>
              </>
            )}
            <span
              className={styles.actionText}
              onClick={(e) => {
                e.stopPropagation();
                openSettings(row);
              }}>
              {t("lbl_auto_consent_open_settings")}
            </span>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, mayDecide]
  );

  const columnVisibility = useMemo(
    () => Object.fromEntries(columns.map((c) => [c.key, true])),
    [columns]
  );

  return (
    <div className={styles.container}>
      <div className={styles.filters}>
        {(
          [
            ["PENDING", "lbl_auto_consent_filter_pending"],
            [undefined, "lbl_auto_consent_filter_all"],
          ] as [OrganizationRequestStatus | undefined, string][]
        ).map(([value, label]) => (
          <button
            key={label}
            type="button"
            className={classNames(styles.filterButton, {
              [styles.filterSelected]: status === value,
            })}
            onClick={() => {
              setStatus(value);
              setPage(1);
            }}>
            {t(label)}
          </button>
        ))}
      </div>

      {isLoading || saving ? (
        <div className={styles.spinner}>
          <Spinner />
        </div>
      ) : rows.length === 0 ? (
        <div className={styles.emptyState}>
          {t("lbl_setting_requests_empty")}
        </div>
      ) : (
        <>
          <Table
            columns={columns}
            columnVisibility={columnVisibility}
            data={rows}
            onRowClick={openSettings}
          />
          <div className={styles.paginationContainer}>
            <Pagination
              countName={t("lbl_setting_requests_tab")}
              pageSize={PAGE_SIZE}
              pagesCount={pageCount}
              page={page}
              length={rows.length}
              handlePaginationChange={setPage}
            />
          </div>
        </>
      )}

      <AutoConsentDecisionModal
        open={pending !== null}
        decision={pending?.decision ?? "approve"}
        waitingLinks={pending?.request.details?.waitingLinks}
        subtitle={
          pending
            ? `${
                pending.request.organizationName ||
                pending.request.organizationCode
              } - ${pending.request.subject ?? ""}`
            : undefined
        }
        handleClose={() => setPending(null)}
        handleConfirm={handleConfirm}
      />
    </div>
  );
};

export default SettingRequestsTab;
